import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../utils/api';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Download, Link as LinkIcon, ArrowsClockwise } from '@phosphor-icons/react';
import { GeneratePaymentLinkDialog } from '../../components/admin/GeneratePaymentLinkDialog';
import { useAuth } from '../../context/AuthContext';
import { formatAnyDate } from '../../utils/dateFormat';

const fmtINR = (n) => '₹ ' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

function MobileQuotationDetail() {
  const { quotation_number } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSales = user?.role === 'sales';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPL, setShowPL] = useState(false);
  const [showResync, setShowResync] = useState(false);
  const [resyncJson, setResyncJson] = useState('');
  const [resyncing, setResyncing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/admin/mobile-quotations/${encodeURIComponent(quotation_number)}`);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [quotation_number]);

  useEffect(() => { load(); }, [load]);

  const doResync = async () => {
    let parsed;
    try {
      parsed = JSON.parse(resyncJson);
    } catch (e) {
      toast.error('Invalid JSON: ' + e.message);
      return;
    }
    if (!parsed.quotation_number) {
      toast.error('JSON must include a "quotation_number" field');
      return;
    }
    setResyncing(true);
    try {
      const { data: res } = await api.post('/api/admin/mobile-inbox/resync-quotation', parsed);
      toast.success(`Resynced ${res.quotation_number} (${res.action}, ${res.items} items)`);
      setShowResync(false);
      setResyncJson('');
      // If the resynced quotation is the one we're viewing, reload
      if (String(res.quotation_number) === String(quotation_number)) {
        await load();
      } else {
        navigate(`/admin/mobile-quotations/${encodeURIComponent(res.quotation_number)}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Resync failed');
    } finally {
      setResyncing(false);
    }
  };

  if (loading || !data) {
    return <Layout><div className="text-center py-12">Loading...</div></Layout>;
  }

  const charges = data.charges || {};
  const items = data.items || [];

  return (
    <Layout>
      <div className="space-y-6 pb-12">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" data-testid="back-btn" onClick={() => navigate('/admin/mobile-quotations')}>
              <ArrowLeft size={18} />
            </Button>
            <div>
              <h1 className="text-2xl font-light tracking-tight text-zinc-950 font-mono">{data.quotation_number}</h1>
              <p className="text-sm text-zinc-500 mt-0.5">{data.customer_name} · {data.customer_mobile}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {!isSales && (
              <Button
                data-testid="resync-btn"
                variant="outline"
                onClick={() => setShowResync(true)}
              >
                <ArrowsClockwise size={16} className="mr-1" /> Resync from JSON
              </Button>
            )}
            <Button
              data-testid="pdf-print-btn"
              variant="outline"
              onClick={() => window.open(`/api/admin/mobile-quotations/${encodeURIComponent(quotation_number)}/pdf`, '_blank')}
            >
              <FileText size={16} className="mr-1" /> Print PDF
            </Button>
            <a href={`/api/admin/mobile-quotations/${encodeURIComponent(quotation_number)}/pdf`} download>
              <Button variant="outline"><Download size={16} className="mr-1" /> Download</Button>
            </a>
            {!isSales && (
              <Button
                data-testid="generate-payment-link-btn"
                onClick={() => setShowPL(true)}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                <LinkIcon size={16} className="mr-1" /> Generate Payment Link
              </Button>
            )}
          </div>
        </div>

        {/* Meta grid */}
        <Card className="border-zinc-300 rounded-sm">
          <CardContent className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <Field label="Quotation Date" value={formatAnyDate(data.quotation_date)} />
            <Field label="Delivery Date" value={formatAnyDate(data.delivery_date)} />
            <Field label="Executive" value={data.executive_name} />
            <Field label="Status" value={<Badge variant="outline" className="uppercase">{data.status}</Badge>} />
            <Field label="Work Location" value={data.work_location} className="col-span-2" />
            <Field label="Total Qty" value={data.total_quantity} />
            <Field label="Total SQFT" value={Number(data.total_sqft || 0).toFixed(2)} />
          </CardContent>
        </Card>

        {/* Lineage */}
        {data.lineage_status && (data.lineage_status.parent || (data.lineage_status.duplicates || []).length > 0 || data.lineage_status.converted_to_jo) && (
          <Card className="border-amber-300 bg-amber-50 rounded-sm">
            <CardHeader className="pb-2"><CardTitle className="text-sm uppercase tracking-wider text-amber-900">Lineage</CardTitle></CardHeader>
            <CardContent className="p-4 space-y-1 text-sm">
              {data.lineage_status.parent && (
                <div>Parent: <Link className="text-blue-600 hover:underline font-mono" to={`/admin/mobile-quotations/${data.lineage_status.parent.quotation_number}`}>{data.lineage_status.parent.quotation_number}</Link></div>
              )}
              {(data.lineage_status.duplicates || []).length > 0 && (
                <div>
                  Duplicates: {data.lineage_status.duplicates.map((d, i) => (
                    <Link key={d.quotation_number} className="text-blue-600 hover:underline font-mono ml-2" to={`/admin/mobile-quotations/${d.quotation_number}`}>{d.quotation_number}</Link>
                  ))}
                </div>
              )}
              {data.lineage_status.converted_to_jo && (
                <div>Converted to JO: <Link className="text-blue-600 hover:underline font-mono" to={`/admin/mobile-job-orders/${data.lineage_status.converted_to_jo}`}>{data.lineage_status.converted_to_jo}</Link></div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Items */}
        <Card className="border-zinc-300 rounded-sm">
          <CardHeader className="border-b border-zinc-300"><CardTitle className="text-lg">Items ({items.length})</CardTitle></CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-zinc-50 border-b border-zinc-300">
                <tr>
                  {['#', 'W', 'H', 'Category', 'Item', 'Profile', 'Shutters', 'Room', 'Opening', 'Qty', 'SQFT', 'Rate', 'Amount'].map(h => (
                    <th key={h} className="text-left p-2 font-bold uppercase text-[10px] text-zinc-700">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={13} className="p-6 text-center text-zinc-400">No items</td></tr>
                ) : items.map((it, i) => {
                  const pick = (...keys) => {
                    for (const k of keys) {
                      const v = it[k];
                      if (v !== undefined && v !== null && v !== '' && v !== 0 && String(v).trim() !== '' && String(v) !== '0') return v;
                    }
                    return '';
                  };
                  // Read dimension in feet — accept feet directly, or convert from mm (1 ft = 304.8 mm)
                  const dimFt = (feetKeys, mmKeys) => {
                    const ft = pick(...feetKeys);
                    const ftNum = Number(ft);
                    if (ft !== '' && !Number.isNaN(ftNum) && ftNum > 0) return ftNum;
                    const mm = pick(...mmKeys);
                    const mmNum = Number(mm);
                    if (mm !== '' && !Number.isNaN(mmNum) && mmNum > 0) return mmNum / 304.8;
                    return 0;
                  };
                  const w = dimFt(
                    ['width', 'w', 'width_ft', 'item_width', 'w_ft', 'window_width'],
                    ['width_mm', 'w_mm', 'window_width_mm'],
                  );
                  const h = dimFt(
                    ['height', 'h', 'height_ft', 'item_height', 'h_ft', 'window_height'],
                    ['height_mm', 'h_mm', 'window_height_mm'],
                  );
                  const cat = pick('category_label', 'category', 'category_name', 'cat', 'type', 'product_type', 'item_category', 'category_code');
                  const item = pick('item_name', 'item', 'itemName', 'description', 'item_desc', 'name', 'item_description', 'product_name');
                  const prof = pick('profile', 'profile_type', 'profile_name', 'color', 'colour', 'profile_color');
                  const shut = pick('no_of_shutters', 'shutters', 'shutter_count', 'num_shutters', 'panels', 'no_of_panels');
                  const room = pick('room_type', 'room', 'location', 'area', 'room_name');
                  const open = pick('opening', 'opening_direction', 'opening_type', 'open_type', 'opening_side');
                  const qty = pick('quantity', 'qty', 'no_of_windows', 'count');
                  const rate = pick('rate', 'final_price', 'selling_price', 'price_per_sqft', 'price', 'unit_rate');
                  return (
                    <tr key={i} className="border-b border-zinc-200">
                      <td className="p-2">{it.sl_no || i + 1}</td>
                      <td className="p-2">{w > 0 ? `${w.toFixed(2)} ft` : ''}</td>
                      <td className="p-2">{h > 0 ? `${h.toFixed(2)} ft` : ''}</td>
                      <td className="p-2">{cat}</td>
                      <td className="p-2 max-w-[280px] whitespace-normal">{item}</td>
                      <td className="p-2">{prof}</td>
                      <td className="p-2">{shut}</td>
                      <td className="p-2">{room}</td>
                      <td className="p-2">{open}</td>
                      <td className="p-2">{qty || 1}</td>
                      <td className="p-2">{Number(it.sqft || 0).toFixed(2)}</td>
                      <td className="p-2">{Number(rate || 0).toFixed(2)}</td>
                      <td className="p-2 font-bold">{fmtINR(it.amount)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>

        {/* Totals */}
        <Card className="border-zinc-300 rounded-sm">
          <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <Row label="Basic" value={fmtINR(data.basic_amount)} />
            <Row label="Discount" value={`- ${fmtINR(charges.discount)}`} />
            <Row label="Installation" value={fmtINR(charges.installation)} />
            <Row label="Transportation" value={fmtINR(charges.transportation)} />
            <Row label="Additional" value={fmtINR(charges.additional)} />
            <Row label="Consumables" value={fmtINR(charges.consumables)} />
            <Row label="Taxable" value={fmtINR(data.taxable_value)} />
            <Row label={`GST (${data.gst_percent || 18}%)`} value={fmtINR(data.gst_amount)} />
            <div className="md:col-span-2 flex justify-between items-center p-3 bg-orange-50 border border-orange-200 rounded-sm">
              <span className="font-bold text-zinc-950 uppercase tracking-wider text-sm">Grand Total</span>
              <span className="font-bold text-orange-700 text-xl">{fmtINR(data.total_amount)}</span>
            </div>
          </CardContent>
        </Card>
      </div>

      <GeneratePaymentLinkDialog
        open={showPL}
        onOpenChange={setShowPL}
        prefill={{
          customer_name: data.customer_name,
          customer_mobile: data.customer_mobile,
          amount: data.total_amount,
          quotation_number: data.quotation_number,
          description: `Payment for Quotation ${data.quotation_number}`,
        }}
      />

      <Dialog open={showResync} onOpenChange={setShowResync}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Resync Quotation from JSON</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-zinc-600">
              Paste the raw JSON payload for a quotation (from your mobile developer or the mobile app&apos;s export).
              We&apos;ll upsert it into the database by <code className="text-orange-600">quotation_number</code> — safe to run multiple times.
            </p>
            <Textarea
              value={resyncJson}
              onChange={(e) => setResyncJson(e.target.value)}
              rows={16}
              placeholder='{"quotation_number":"Q-2026-0332","customer_name":"...","items":[ ... ]}'
              className="font-mono text-xs"
              data-testid="resync-json-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResync(false)}>Cancel</Button>
            <Button
              onClick={doResync}
              disabled={resyncing || !resyncJson.trim()}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="resync-submit-btn"
            >
              {resyncing ? 'Resyncing…' : 'Resync Now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

function Field({ label, value, className = '' }) {
  return (
    <div className={className}>
      <Label className="text-xs uppercase text-zinc-500">{label}</Label>
      <div className="text-zinc-950 text-sm mt-0.5">{value || '—'}</div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between border-b border-zinc-100 py-2">
      <span className="text-zinc-600">{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  );
}

export default MobileQuotationDetail;
export { MobileQuotationDetail };
